<template>
  <transition name="elements-animation">
    <div v-show="showForm" class="view-input-form">
      <h3>View Details</h3>

      <p class="form-field">View Type</p>
      <Dropdown class="dropdown-menu" v-on:selection="setSelectedViewType($event)"/>

      <InputField
        v-model="name"
        :note="viewNameDescription"
        :defaultValueNote="viewClassNameNote"
        :additionalNote="viewFileNameNote"
        :title="'Name'"
        :placeholder="'View Name'"
        v-on:keyup="enterPressed"
        class="form-field"
      />
      <p v-show="hasValidationMessage" class="validation-message">{{ validationMessage }}</p>

      <Checkbox
        v-model="startingView"
        :value="startingView"
        v-bind:title="'Set as Starting View'"
        v-bind:description="'This is the first view you see when you open the app'"
      />

      <Checkbox
        v-show="isShowingEmpty"
        :value="showTemplateUi"
        v-model="showTemplateUi"
        v-bind:title="'Show template UI'"
        v-bind:description="'The UI displayed will be an example of the UI helpers and widgets provided by the template'"
      />

      <button
        v-show="!updatingView"
        class="invert-cta"
        :class="{'disabled-button' : !canAddView}"
        v-on:click="addView"
      >Add view</button>

      <button
        class="invert-cta"
        :class="{'disabled-button' : !hasUpdatedDetails}"
        v-show="updatingView"
        v-on:click="updateView"
      >Update view</button>
    </div>
  </transition>
</template>

<script>
import { mapMutations, mapState, mapGetters, mapActions } from "vuex";
import InputField from "@/components/InputField.vue";
import Dropdown from "@/components/Dropdown.vue";
import Checkbox from "@/components/Checkbox.vue";

export default {
  name: "ViewInputForm",
  props: {
    showForm: Boolean
  },
  components: {
    InputField,
    Dropdown,
    Checkbox
  },
  computed: {
    ...mapState(["selectedView", "views"]),
    ...mapGetters(["hasViewWithName"]),
    canAddView() {
      return !this.updatingView && !this.hasValidationMessage && this.name != '';
    },
    isShowingEmpty() {
      return this.selectedViewType.type === "empty";
    },
    hasUpdatedDetails() {
      return (
        this.hasUpdatedStartingValue ||
        this.hasUpdatedName ||
        this.hasUpdatedShowTemplateUi
      );
    },
    hasUpdatedStartingValue() {
      return this.selectedView.start != this.startingView;
    },
    hasUpdatedShowTemplateUi() {
      return this.selectedView.showTemplateUi != this.showTemplateUi;
    },
    hasUpdatedName() {
      return this.selectedView.name != this.name;
    },
    hasValidationMessage() {
      return this.validationMessage != "";
    },
    updatingView() {
      return this.selectedView.index != undefined;
    },
    viewClassNameNote() {
      return this.name == "" ? "" : "Class Name: " + this.viewClassName;
    },
    viewClassName() {
      return this.name + "View";
    },
    viewFileNameNote() {
      return this.name == "" ? "" : "File Name: " + this.viewFileName;
    },
    viewFileName() {
      var lowerUnderscoreFilename = this.viewClassName
        .split(/(?=[A-Z])/)
        .filter(function(el) {
          return el != null;
        })
        .map(value => {
          return value.toLowerCase();
        })
        .join("_");

      return lowerUnderscoreFilename + ".dart";
    },
    viewNameDescription() {
      return this.selectedViewType.fixedName
        ? "This view has a suggested name. We recommend you use the given name to keep consistency with our templates."
        : "Provide the name of the view you want. Provide only the name, don't add any descriptors like View or Page behind it. Your full viewname can be seen below.";
    }
  },
  methods: {
    ...mapActions([
      "addViewToTemplate"
    ]),
    ...mapMutations([
      "UPDATE_VIEW",
      "CLEAR_SELECTED_VIEW",
      "HAS_VIEW_WITH_NAME"
    ]),
    enterPressed() {
      if (!this.updatingView) {
        this.addView();
      } else {
        this.updateView();
      }
    },
    startsWithCapital(value) {
      return value[0] === value[0].toUpperCase();
    },
    longerThanTwoLetters(value) {
      return value.length >= 2;
    },
    hasNoSpaces(value) {
      return value.indexOf(" ") < 0;
    },
    hasNoSameNamedView() {
      var hasView = true;

      this.views.forEach(view => {
        if (view.name === this.name) {
          hasView = false;
        }
      });

      return hasView;
    },
    getValidationMessage() {
      if (this.name == "" || this.name == undefined) {
        return "";
      }

      var re = /[\W_\d]+/g;
      var test = re.test(this.name);
      // console.log(`${test} : ${JSON.stringify(test)}`);
      if (test) {
        return "No alphanumeric letters allowed in class name.";
      }

      if (!this.startsWithCapital(this.name)) {
        return "Please start your view name with a capital letter.";
      }

      if (!this.longerThanTwoLetters(this.name)) {
        return "It's recommended that your view name is more than 2 letters.";
      }

      if (!this.hasNoSpaces(this.name)) {
        return "View names cannot have any spaces in it";
      }

      if (!this.hasNoSameNamedView(this.name) && !this.updatingView) {
        return "A view with this name already exists. Please enter a different name";
      }

      if (this.updatingView) {
        if (!this.hasUpdatedDetails) {
          return "You have to change something to update a view.";
        }

        if (!this.hasNoSameNamedView(this.name) && this.hasUpdatedName) {
          return "A view with this name already exists. Please enter a different name";
        }
      }

      // console.log("At the end");
      return "";
    },
    addView() {
      if (!this.hasValidationMessage && this.name != "") {
        this.addViewToTemplate({
          type: this.selectedViewType.type,
          name: this.name,
          fileName: this.viewFileName,
          start: this.startingView,
          showTemplateUi: this.showTemplateUi,
          packageDependencies: this.selectedViewType.packageDependencies
        });
      } else {
        this.validationMessage = this.getValidationMessage();
      }

      this.resetViewValues();
    },
    updateView() {
      if (!this.hasValidationMessage && this.name != "") {
        this.UPDATE_VIEW({
          index: this.selectedView.index,
          type: this.selectedView.type,
          name: this.name,
          start: this.startingView,
          fileName: this.viewFileName
        });

        this.resetViewValues();
      }
    },
    resetViewValues() {
      this.name = "";
      this.selectedViewType = this.startingViewType;
      this.startingView = false;
      this.showTemplateUi = false;
    },
    setSelectedViewType(value) {
      // console.log(value);
      this.selectedViewType = value;
      if (value.fixedName === true) {
        this.name = value.suggestedName;
      } else {
        this.name = "";
      }

      if (this.startingViewType !== {}) {
        this.startingViewType = value;
      }
    },
    toggleShowForm() {
      this.CLEAR_SELECTED_VIEW();
      this.$emit("toggleForm");
    }
  },
  watch: {
    selectedView: function(value) {
      if (value.name !== undefined && value.name !== "") {
        this.name = value.name;
        this.startingView = value.start;
      } else {
        this.resetViewValues();
      }
    },
    startingView: function(value) {
      this.validationMessage = this.getValidationMessage();
    },
    showTemplateUi: function(value) {
      this.validationMessage = this.getValidationMessage();
    },
    name: function(value) {
      this.validationMessage = this.getValidationMessage();
    }
  },
  data() {
    return {
      name: "",
      selectedViewType: {},
      validationMessage: "",
      startingView: false,
      startingViewType: {},
      showTemplateUi: false
    };
  }
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/styles.scss";

$views-input-form-padding: 40px;

.dropdown-menu {
  margin-top: 15px;
}

.disabled-button {
  background-color: $light-grey-color;
  color: $medium-grey-color;
  cursor: default;
}

.validation-message {
  color: $validation-message-color;
  font-weight: 400;
  margin-top: 10px;
}

option {
  height: $input-field-height;
}

$action-button-padding: 10px;

button {
  margin-top: 40px;

  cursor: pointer;
}

.view-input-form {
  box-sizing: border-box;
  padding-left: $views-input-form-padding;
  padding-bottom: $views-input-form-padding;
  padding-right: $views-input-form-padding;
  width: 80%;
}

p {
  font-weight: 800;
  font-size: $input-title-size;
}

select {
  width: $input-field-width;
  height: $input-field-height;

  background: none;
  border: none;
  border-radius: 5px;
  color: white;

  background-color: $light-grey-color;
}
</style>
